import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { vaultReaderApi } from "@/lib/api-client";
import { toast } from "sonner";
import { ArrowLeft, Archive, ExternalLink, Eye, Loader2, X, ShieldCheck, ScanSearch, FilePlus2, Download, Tags, ArrowUpDown, Link2 } from "lucide-react";

// Sovereign Seal — elegant gold wax-seal mark, subtle glow on hover
export const WaxSeal = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" className="uc-seal" data-testid="sovereign-seal-icon">
    <path
      d="M12 2.2l1.7 1.2 2-.5 1 1.8 2 .4.1 2.1 1.6 1.3-.9 1.9.9 1.9-1.6 1.3-.1 2.1-2 .4-1 1.8-2-.5L12 20.6l-1.7-1.2-2 .5-1-1.8-2-.4-.1-2.1-1.6-1.3.9-1.9-.9-1.9 1.6-1.3.1-2.1 2-.4 1-1.8 2 .5L12 2.2z"
      fill="#B8860B" stroke="#F59E0B" strokeWidth="0.6" />
    <circle cx="12" cy="11.4" r="5.2" fill="none" stroke="#FCD34D" strokeWidth="0.9" opacity="0.9" />
    <path d="M9.6 11.4l1.7 1.7 3.1-3.4" fill="none" stroke="#FEF3C7" strokeWidth="1.3"
          strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const SOURCES = [
  { v: null, label: "All Sources" },
  { v: "vault", label: "Executed Vault" },
  { v: "github", label: "GitHub" },
  { v: "gmail", label: "Gmail" },
];
const TYPES = [
  { v: null, label: "All Types" },
  { v: "contract", label: "Contracts" },
  { v: "email", label: "Emails" },
  { v: "draft", label: "Drafts" },
  { v: "document", label: "Documents" },
];


export default function BoardroomVault() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [source, setSource] = useState(null);
  const [type, setType] = useState(null);
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [quick, setQuick] = useState(null);
  const [quickBusy, setQuickBusy] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [briefBusy, setBriefBusy] = useState(null);
  const [briefs, setBriefs] = useState({});
  const [webhooks, setWebhooks] = useState([]);
  const [patches, setPatches] = useState([]);
  const [patchForm, setPatchForm] = useState({ agent: "executor", capability: "" });
  const [patchBusy, setPatchBusy] = useState(null);

  const loadPatches = () => vaultReaderApi.patches().then((r) => setPatches(r.patches || [])).catch(() => {});

  const proposePatch = async () => {
    if (!patchForm.capability.trim()) return;
    try {
      const r = await vaultReaderApi.proposePatch(patchForm.agent, patchForm.capability);
      toast.success(`Patch ${r.status} — ${r.library || r.patch_id}`);
      setPatchForm((f) => ({ ...f, capability: "" }));
      loadPatches();
    } catch (e) { toast.error(e?.response?.data?.detail || "Patch proposal failed"); }
  };

  const approvePatch = async (p) => {
    setPatchBusy(p.id);
    try {
      const r = await vaultReaderApi.approvePatch(p.id);
      toast.success(`PATCH APPLIED — ${p.agent} now carries '${p.capability}' (${r.evidence})`);
      loadPatches();
    } catch (e) { toast.error(e?.response?.data?.detail || "Patch approval failed"); }
    finally { setPatchBusy(null); }
  };
  const [censusBusy, setCensusBusy] = useState(false);
  const [census, setCensus] = useState(null);
  const [fileOpen, setFileOpen] = useState(false);
  const [fileBusy, setFileBusy] = useState(false);
  const [cf, setCf] = useState({ title: "", parties: "", signed_date: "", status: "SIGNED", body: "" });

  const [dlBusy, setDlBusy] = useState(null);
  const [sortKey, setSortKey] = useState(null); // "venture" | "scope"
  const [sortDir, setSortDir] = useState(1);
  const [retroBusy, setRetroBusy] = useState(false);
  const [ventureFilter, setVentureFilter] = useState(null);
  const [scopeFilter, setScopeFilter] = useState(null);
  const [triage, setTriage] = useState(false);
  const [triageSel, setTriageSel] = useState({});
  const [triageVenture, setTriageVenture] = useState("UMBRELLA");
  const [triageBusy, setTriageBusy] = useState(false);
  const [leaders, setLeaders] = useState([]);
  const [sweep, setSweep] = useState(null); // {pending, breakdown}
  const [sweepBusy, setSweepBusy] = useState(false);
  const [approveBusy, setApproveBusy] = useState(false);

  useEffect(() => {
    vaultReaderApi.roiLeaderboard().then((r) => setLeaders(r.leaders || [])).catch(() => {});
  }, []);

  const runSweep = async () => {
    setSweepBusy(true);
    toast.info("Oracle Sweep — proposing venture assignments for every unsorted artifact…");
    try {
      const r = await vaultReaderApi.triageSweep();
      setSweep({ pending: r.proposals, breakdown: r.breakdown });
      toast.success(`Oracle proposed ${r.proposals} assignment(s) — ${Object.entries(r.breakdown || {}).map(([k, v]) => `${k} ${v}`).join(", ")}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sweep failed");
    } finally { setSweepBusy(false); }
  };

  const approveSweep = async () => {
    setApproveBusy(true);
    try {
      const r = await vaultReaderApi.triageSweepApprove();
      toast.success(`Sweep approved — ${r.applied} artifact(s) reassigned in one pass`);
      setSweep(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approve failed");
    } finally { setApproveBusy(false); }
  };

  const VENTURES = ["GIFTED-OS", "CHECKMATE", "BRAND-SIGNAL", "WONDER-WORLD", "UMBRELLA"];

  const toggleTriage = () => {
    setTriage((t) => {
      const next = !t;
      setVentureFilter(next ? "LEGACY_UNSORTED" : null);
      setTriageSel({});
      if (next) vaultReaderApi.triageSweepPending().then((r) => setSweep(r.pending ? r : null)).catch(() => {});
      return next;
    });
  };

  const applyTriage = async () => {
    const ids = Object.keys(triageSel).filter((k) => triageSel[k]);
    if (!ids.length) return;
    setTriageBusy(true);
    try {
      const r = await vaultReaderApi.reassign(ids, triageVenture);
      toast.success(`Legacy Triage — ${r.reassigned} artifact(s) reassigned to ${r.venture}`);
      setTriageSel({});
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reassign failed");
    } finally { setTriageBusy(false); }
  };

  const toggleSort = (key) => {
    if (sortKey === key) { setSortDir((d) => -d); } else { setSortKey(key); setSortDir(1); }
  };

  const retroTag = async () => {
    setRetroBusy(true);
    try {
      const r = await vaultReaderApi.retroTag();
      toast.success(`Retro-Tag complete — ${r.tagged} classified, ${r.legacy_unsorted} LEGACY_UNSORTED, ${r.duplicates_mapped} duplicate(s) mapped to Master Records, ${r.roi_linked} win(s) ROI-linked (${r.scanned} scanned)`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Retro-Tag failed");
    } finally { setRetroBusy(false); }
  };

  const downloadArtifact = async (r) => {
    setDlBusy(r.id);
    try {
      const params = {};
      if (r.source === "github") params.path = r.metadata?.path;
      if (r.source === "gmail") { params.uid = r.metadata?.uid; params.mailbox = r.metadata?.mailbox; }
      const resp = await vaultReaderApi.download(r.id, params);
      const cd = resp.headers["content-disposition"] || "";
      const m = cd.match(/filename="([^"]+)"/);
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = m ? m[1] : `${r.title}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Artifact extracted — download started");
    } catch (e) {
      let detail = "Download failed";
      try { detail = JSON.parse(await e?.response?.data?.text())?.detail || detail; } catch {}
      toast.error(detail);
    } finally { setDlBusy(null); }
  };

  const fileContract = async () => {
    setFileBusy(true);
    try {
      const r = await vaultReaderApi.fileContract({
        title: cf.title, parties: cf.parties || undefined,
        signed_date: cf.signed_date || undefined, status: cf.status, body: cf.body,
      });
      toast.success(`Contract filed — ${r.path} (commit ${String(r.commit).slice(0, 10)}). Run Full Census to convert obligations into Live Mandates.`);
      setFileOpen(false);
      setCf({ title: "", parties: "", signed_date: "", status: "SIGNED", body: "" });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Contract filing failed");
    } finally { setFileBusy(false); }
  };

  const runCensus = async () => {
    setCensusBusy(true);
    toast.info("Full Census — the Auditor is reading every GitHub vault/contract document…");
    try {
      const r = await vaultReaderApi.census();
      setCensus(r);
      toast.success(`Census complete — ${r.documents_found} document(s), ${r.contracts_found} contract(s), ${(r.new_mandates || []).length} new Live Mandate(s) filed to the Action Hub`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Census failed");
    } finally { setCensusBusy(false); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await vaultReaderApi.artifacts({ source, artifact_type: type, verified: verifiedOnly || undefined }));
    } catch (e) { toast.error("Vault read failed"); }
    finally { setLoading(false); }
  }, [source, type, verifiedOnly]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    vaultReaderApi.webhooks().then((r) => setWebhooks(r.webhooks || [])).catch(() => {});
    vaultReaderApi.patches().then((r) => setPatches(r.patches || [])).catch(() => {});
  }, []);

  const issueBrief = async (r) => {
    setBriefBusy(r.id);
    try {
      const payload = { artifact_id: r.id, title: r.title };
      if (r.source === "github" && r.metadata?.path && /\.(md|markdown|txt)$/i.test(r.metadata.path)) payload.path = r.metadata.path;
      else payload.content = `Parties: ${r.metadata?.parties || "unknown"}. Status: ${r.metadata?.status || "unknown"}. Attachments: ${(r.metadata?.attachments || []).join(", ") || "none"}.`;
      const b = await vaultReaderApi.brief(payload);
      setBriefs((m) => ({ ...m, [r.id]: b }));
      toast.success("Auditor Mandate executed — Core Obligations briefed");
    } catch (e) { toast.error(e?.response?.data?.detail || "Auditor Mandate failed"); }
    finally { setBriefBusy(null); }
  };

  const quickLook = async (row) => {
    if (row.source !== "github" || !row.metadata?.path) return;
    setQuickBusy(true);
    try { setQuick(await vaultReaderApi.quicklook(row.metadata.path)); }
    catch (e) { toast.error(e?.response?.data?.detail || "Quick-Look unavailable"); }
    finally { setQuickBusy(false); }
  };

  const s = data?.sources || {};
  const chip = (st, name) => {
    const ok = st?.configured && st?.error === undefined && (st?.errors === undefined || !st.errors);
    return (
      <span key={name} className="uc-chip flex items-center gap-1.5" data-testid={`vault-source-${name}`}>
        <span className={`uc-dot ${st?.configured ? (ok ? "bg-emerald-400" : "bg-rose-400") : "bg-white/25"}`}></span>
        {name}
        {st?.configured && ok && st?.count !== undefined ? (
          <span className="text-emerald-300"> · verified · {st.count}</span>
        ) : st?.configured && !ok ? (
          <span className="text-rose-300" title={st?.error || ""}> · error</span>
        ) : !st?.configured ? " · standby" : (st?.count !== undefined ? ` · ${st.count}` : "")}
      </span>
    );
  };
  const verifiedCount = ["vault", "github", "gmail"].reduce((n, k) => {
    const st = s[k];
    const ok = st?.configured && st?.error === undefined && (st?.errors === undefined || !st.errors);
    return n + (ok && typeof st?.count === "number" ? st.count : 0);
  }, 0);

  return (
    <div className="min-h-screen uc-grain relative" data-testid="boardroom-vault">
      <style>{`.uc-seal{filter:drop-shadow(0 0 2px rgba(245,158,11,.35));transition:filter .3s ease,transform .3s ease}
        .uc-seal-wrap:hover .uc-seal{filter:drop-shadow(0 0 8px rgba(245,158,11,.9));transform:scale(1.12)}`}</style>
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-6">
        <header className="flex items-center gap-4 flex-wrap mb-6">
          <Link to="/" data-testid="vault-back-link"
                className="uc-chip flex items-center gap-1.5 hover:bg-white/5"><ArrowLeft size={11}/> Dashboard</Link>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 border border-amber-500/40 bg-amber-500/10 flex items-center justify-center">
              <Archive size={15} className="text-amber-400" />
            </div>
            <div>
              <div className="font-mono-uc text-[10px] uppercase tracking-widest text-amber-400">The Boardroom Vault</div>
              <h1 className="font-serif-display text-2xl leading-none">Artifact Reader</h1>
            </div>
          </div>
          <div className="ml-auto flex items-center gap-2 flex-wrap" data-testid="vault-source-strip">
            <span className="uc-chip flex items-center gap-1.5 border-emerald-500/40 text-emerald-300"
                  data-testid="verified-artifact-count"
                  title="Total artifacts across live, error-free sources — this is the truthful verified inventory">
              <ShieldCheck size={10}/> Verified Artifacts · {verifiedCount}
            </span>
            <button data-testid="vault-file-contract-btn" onClick={() => setFileOpen(true)}
                    title="File a real signed contract into sovereign-vault/contracts"
                    className="uc-chip flex items-center gap-1.5 hover:border-emerald-500/60 hover:bg-emerald-500/10 transition-colors text-emerald-300">
              <FilePlus2 size={10}/> File Contract
            </button>
            <button data-testid="vault-census-btn" onClick={runCensus} disabled={censusBusy}
                    title="Vault-to-Action — full GitHub census: every document briefed, every date/liability becomes a Live Mandate"
                    className="uc-chip flex items-center gap-1.5 hover:border-amber-500/60 hover:bg-amber-500/10 transition-colors text-amber-400 disabled:opacity-50">
              {censusBusy ? <Loader2 size={10} className="animate-spin"/> : <ScanSearch size={10}/>}
              Full Census
            </button>
            <button data-testid="vault-retro-tag-btn" onClick={retroTag} disabled={retroBusy}
                    title="Zero-Gap Cleanup — Retro-Tag every untagged artifact from its contents; unclassifiable entries become LEGACY_UNSORTED"
                    className="uc-chip flex items-center gap-1.5 hover:border-sky-500/60 hover:bg-sky-500/10 transition-colors text-sky-300 disabled:opacity-50">
              {retroBusy ? <Loader2 size={10} className="animate-spin"/> : <Tags size={10}/>}
              Retro-Tag
            </button>
            {chip(s.vault, "vault")}{chip(s.github, "github")}{chip(s.gmail, "gmail")}
            <span className="uc-chip flex items-center gap-1.5 text-amber-300" data-testid="vault-sealed-count">
              <ShieldCheck size={10}/> {data?.sealed_count ?? 0} sealed
            </span>
          </div>
        </header>

        {census && (
          <div className="mb-4 border border-amber-500/25 bg-amber-500/[0.03] px-4 py-3" data-testid="census-result">
            <div className="font-mono-uc text-[10px] uppercase tracking-widest text-amber-400 mb-1.5">
              Vault-to-Action Census · {census.repo}
            </div>
            <div className="font-mono-uc text-[11px] text-white/70">
              {census.documents_found} document(s) read · {census.contracts_found} contract(s) ·{" "}
              {(census.briefs || []).length} Core Obligation Brief(s) ·{" "}
              <span className="text-amber-300">{(census.new_mandates || []).length} new Live Mandate(s) in the Action Hub</span>
            </div>
            {(census.new_mandates || []).map((m, i) => (
              <div key={i} className="font-mono-uc text-[10px] text-white/60 mt-1" data-testid={`census-mandate-${i}`}>
                → {m.obligation}{m.date ? ` · deadline ${m.date}` : ""}{m.amount_usd ? ` · $${m.amount_usd.toLocaleString()}` : ""}
              </div>
            ))}
            {census.note && <div className="font-mono-uc text-[10px] text-white/45 mt-1.5">{census.note}</div>}
          </div>
        )}

        <div className="flex items-center gap-2 flex-wrap mb-4" data-testid="vault-filters">
          {SOURCES.map((o) => (
            <button key={o.label} data-testid={`vault-filter-source-${o.v || "all"}`}
                    onClick={() => setSource(o.v)}
                    className={`uc-chip transition-colors ${source === o.v ? "border-amber-500/60 text-amber-300 bg-amber-500/10" : "hover:bg-white/5"}`}>
              {o.label}
            </button>
          ))}
          <span className="w-px h-4 bg-white/10 mx-1"></span>
          {TYPES.map((o) => (
            <button key={o.label} data-testid={`vault-filter-type-${o.v || "all"}`}
                    onClick={() => setType(o.v)}
                    className={`uc-chip transition-colors ${type === o.v ? "border-amber-500/60 text-amber-300 bg-amber-500/10" : "hover:bg-white/5"}`}>
              {o.label}
            </button>
          ))}
          <span className="w-px h-4 bg-white/10 mx-1"></span>
          <button data-testid="vault-filter-verified" onClick={() => setVerifiedOnly((v) => !v)}
                  className={`uc-chip flex items-center gap-1.5 transition-colors ${verifiedOnly ? "border-amber-500/70 text-amber-300 bg-amber-500/10" : "hover:bg-white/5"}`}>
            <WaxSeal size={12}/> Verified · Sovereign Seal
          </button>
        </div>

        {/* ROI LEADERBOARD — highest-leverage artifacts by ROI-per-credit */}
        {leaders.length > 0 && (
          <section className="mb-4 border border-emerald-500/25 bg-emerald-500/[0.03] px-4 py-3" data-testid="roi-leaderboard">
            <div className="font-mono-uc text-[10px] uppercase tracking-widest text-emerald-300 mb-2">
              ROI Leaderboard · top earners by return-per-credit <span className="text-white/30 normal-case">(est. cost basis)</span>
            </div>
            <div className="flex gap-2 overflow-x-auto uc-scroll pb-1">
              {leaders.map((l, i) => (
                <a key={l.id} href={`${process.env.REACT_APP_BACKEND_URL}${l.download}`} target="_blank" rel="noreferrer"
                   data-testid={`roi-leader-${i}`}
                   title={`${l.clinical_name} — $${l.roi_value} realized on ~${l.cost_credits}cr`}
                   className="flex-none w-56 border border-white/10 bg-black/40 p-2.5 hover:border-emerald-500/50 transition-colors">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-mono-uc text-[9px] text-emerald-300 border border-emerald-500/40 px-1">#{i + 1}</span>
                    <span className="font-mono-uc text-[8px] uppercase tracking-widest text-white/40 truncate">{l.venture} · {l.scope}</span>
                  </div>
                  <div className="font-mono-uc text-[10px] text-white/80 truncate">{l.clinical_name}</div>
                  <div className="flex items-baseline gap-2 mt-1">
                    <span className="font-serif-display text-lg text-emerald-300">${l.leverage.toLocaleString()}</span>
                    <span className="font-mono-uc text-[8px] text-white/40">per credit</span>
                    <span className="font-mono-uc text-[9px] text-white/50 ml-auto">${l.roi_value} / {l.cost_credits}cr</span>
                  </div>
                </a>
              ))}
            </div>
          </section>
        )}

        <section className="uc-card p-0 overflow-hidden" data-testid="vault-artifact-table">
          <div className="px-4 py-2 border-b border-white/10 flex items-center gap-2 flex-wrap">
            <span className="font-mono-uc text-[10px] uppercase tracking-widest text-amber-400">Sovereign Registry</span>
            <span className="font-mono-uc text-[9px] text-white/30">clinical taxonomy · [VENTURE]_[SCOPE]_[DATE]_[NAME]</span>
            <button data-testid="vault-triage-btn" onClick={toggleTriage}
                    title="Legacy Triage — review LEGACY_UNSORTED artifacts and reassign them to the right venture in bulk"
                    className={`ml-auto uc-chip flex items-center gap-1.5 transition-colors ${triage
                      ? "border-amber-500/70 text-amber-300 bg-amber-500/10"
                      : "hover:border-amber-500/60 hover:bg-amber-500/10 text-white/60"}`}>
              <Tags size={10}/> Legacy Triage · {(data?.artifacts || []).filter((r) => r.venture === "LEGACY_UNSORTED").length}
            </button>
          </div>
          {/* REGISTRY FILTERS — isolate a venture's entire paper trail instantly */}
          <div className="px-4 py-2 border-b border-white/10 flex items-center gap-1.5 flex-wrap" data-testid="registry-filters">
            <span className="font-mono-uc text-[8px] uppercase tracking-widest text-white/30 mr-1">Venture</span>
            {[...new Set((data?.artifacts || []).map((r) => r.venture).filter(Boolean))].sort().map((v) => (
              <button key={v} data-testid={`registry-filter-venture-${v}`}
                      onClick={() => setVentureFilter((f) => (f === v ? null : v))}
                      className={`font-mono-uc text-[8px] uppercase tracking-widest px-1.5 py-0.5 border transition-colors ${
                        ventureFilter === v ? "border-amber-500/70 text-amber-300 bg-amber-500/10" : "border-white/15 text-white/45 hover:border-amber-500/40"}`}>
                {v}
              </button>
            ))}
            <span className="w-px h-3 bg-white/10 mx-1"></span>
            <span className="font-mono-uc text-[8px] uppercase tracking-widest text-white/30 mr-1">Scope</span>
            {[...new Set((data?.artifacts || []).map((r) => r.scope).filter(Boolean))].sort().map((sc) => (
              <button key={sc} data-testid={`registry-filter-scope-${sc}`}
                      onClick={() => setScopeFilter((f) => (f === sc ? null : sc))}
                      className={`font-mono-uc text-[8px] uppercase tracking-widest px-1.5 py-0.5 border transition-colors ${
                        scopeFilter === sc ? "border-sky-500/70 text-sky-300 bg-sky-500/10" : "border-white/15 text-white/45 hover:border-sky-500/40"}`}>
                {sc}
              </button>
            ))}
            {(ventureFilter || scopeFilter) && (
              <button data-testid="registry-filter-clear" onClick={() => { setVentureFilter(null); setScopeFilter(null); }}
                      className="font-mono-uc text-[8px] uppercase tracking-widest px-1.5 py-0.5 border border-rose-500/40 text-rose-300 hover:bg-rose-500/10">
                Clear
              </button>
            )}
          </div>
          {/* LEGACY TRIAGE LANE — one-click bulk reassignment */}
          {triage && (
            <div className="px-4 py-2 border-b border-amber-500/25 bg-amber-500/[0.04] flex items-center gap-2 flex-wrap" data-testid="triage-lane">
              <span className="font-mono-uc text-[9px] uppercase tracking-widest text-amber-300">
                Triage · {Object.values(triageSel).filter(Boolean).length} selected
              </span>
              <button data-testid="triage-select-all"
                      onClick={() => {
                        const all = {};
                        (data?.artifacts || []).filter((r) => r.venture === "LEGACY_UNSORTED" && r.source === "vault")
                          .forEach((r) => { all[r.id] = true; });
                        setTriageSel(all);
                      }}
                      className="uc-chip text-white/60 hover:bg-white/5">Select all unsorted</button>
              <span className="font-mono-uc text-[9px] text-white/40">reassign to</span>
              <select data-testid="triage-venture-select" value={triageVenture}
                      onChange={(e) => setTriageVenture(e.target.value)}
                      className="bg-black border border-white/15 px-2 py-1 text-[10px] font-mono-uc text-white/90 outline-none">
                {VENTURES.map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
              <button data-testid="triage-apply-btn" onClick={applyTriage}
                      disabled={triageBusy || !Object.values(triageSel).some(Boolean)}
                      className="uc-chip flex items-center gap-1.5 text-emerald-300 border-emerald-500/40 hover:bg-emerald-500/10 disabled:opacity-40">
                {triageBusy ? <Loader2 size={10} className="animate-spin"/> : <ShieldCheck size={10}/>}
                Reassign selected
              </button>
              <span className="w-px h-4 bg-white/10 mx-1"></span>
              <button data-testid="triage-sweep-btn" onClick={runSweep} disabled={sweepBusy}
                      title="Bulk Triage Sweep — the Oracle proposes a venture for EVERY unsorted artifact; you approve in one pass"
                      className="uc-chip flex items-center gap-1.5 text-amber-300 border-amber-500/40 hover:bg-amber-500/10 disabled:opacity-50">
                {sweepBusy ? <Loader2 size={10} className="animate-spin"/> : <ScanSearch size={10}/>}
                Oracle Sweep
              </button>
              {sweep?.pending > 0 && (
                <>
                  <span className="font-mono-uc text-[9px] text-white/50" data-testid="triage-sweep-breakdown">
                    {Object.entries(sweep.breakdown || {}).map(([k, v]) => `${k} ${v}`).join(" · ")}
                  </span>
                  <button data-testid="triage-sweep-approve-btn" onClick={approveSweep} disabled={approveBusy}
                          className="uc-chip flex items-center gap-1.5 text-emerald-300 border-emerald-500/60 bg-emerald-500/10 hover:bg-emerald-500/20 disabled:opacity-50">
                    {approveBusy ? <Loader2 size={10} className="animate-spin"/> : <ShieldCheck size={10}/>}
                    Approve all {sweep.pending}
                  </button>
                </>
              )}
            </div>
          )}
          <div className="grid grid-cols-[24px_minmax(0,1fr)_108px_84px_76px_64px_74px_70px] gap-2 px-4 py-2.5 border-b border-white/10 font-mono-uc text-[9px] uppercase tracking-widest text-white/40">
            <span></span>
            <span>Artifact</span>
            <button data-testid="registry-sort-venture" onClick={() => toggleSort("venture")}
                    className={`flex items-center gap-1 uppercase tracking-widest text-left hover:text-amber-300 transition-colors ${sortKey === "venture" ? "text-amber-400" : ""}`}>
              Venture <ArrowUpDown size={8}/>
            </button>
            <button data-testid="registry-sort-scope" onClick={() => toggleSort("scope")}
                    className={`flex items-center gap-1 uppercase tracking-widest text-left hover:text-amber-300 transition-colors ${sortKey === "scope" ? "text-amber-400" : ""}`}>
              Scope <ArrowUpDown size={8}/>
            </button>
            <span>Date</span><span title="Cost of Production — estimated credits spent producing this artifact">Cost</span>
            <span title="Return on Investment — realized dollar value tied to this artifact">ROI</span><span></span>
          </div>
          {loading && <div className="px-4 py-8 text-white/40 text-sm flex items-center gap-2"><Loader2 size={13} className="animate-spin"/> Reading the vault…</div>}
          {!loading && !(data?.artifacts || []).length && (
            <div className="px-4 py-8 text-white/40 text-sm" data-testid="vault-empty">
              No artifacts match. GitHub/Gmail sources activate the moment their credentials land in the estate.
            </div>
          )}
          <div className="max-h-[62vh] overflow-y-auto uc-scroll">
            {(() => {
              let rows = [...(data?.artifacts || [])];
              if (ventureFilter) rows = rows.filter((r) => r.venture === ventureFilter);
              if (scopeFilter) rows = rows.filter((r) => r.scope === scopeFilter);
              if (sortKey) rows.sort((a, b) => String(a[sortKey] || "\uffff").localeCompare(String(b[sortKey] || "\uffff")) * sortDir);
              return rows;
            })().map((r) => {
              const brief = briefs[r.id] || r.brief;
              const isOpen = expanded === r.id;
              const vc = {
                "GIFTED-OS": "border-sky-500/40 text-sky-300",
                "CHECKMATE": "border-emerald-500/40 text-emerald-300",
                "BRAND-SIGNAL": "border-rose-500/40 text-rose-300",
                "WONDER-WORLD": "border-cyan-500/40 text-cyan-300",
                "UMBRELLA": "border-amber-500/40 text-amber-300",
                "LEGACY_UNSORTED": "border-white/15 text-white/35",
              }[r.venture] || "border-white/10 text-white/30";
              return (
              <div key={r.id}>
              <div data-testid={`vault-row-${r.id}`} onClick={() => setExpanded(isOpen ? null : r.id)}
                   className={`grid grid-cols-[24px_minmax(0,1fr)_108px_84px_76px_64px_74px_70px] gap-2 px-4 py-2 border-b border-white/5 items-center hover:bg-white/[0.03] transition-colors cursor-pointer ${r.verified ? "bg-amber-500/[0.04]" : ""}`}>
                <span className="uc-seal-wrap" title={r.verified ? "Sovereign Seal — verified in both GitHub and Gmail" : ""}
                      onClick={(e) => e.stopPropagation()}>
                  {triage && r.venture === "LEGACY_UNSORTED" && r.source === "vault" ? (
                    <input type="checkbox" data-testid={`triage-check-${r.id}`}
                           checked={!!triageSel[r.id]}
                           onChange={(e) => setTriageSel((s) => ({ ...s, [r.id]: e.target.checked }))}
                           className="accent-amber-500 cursor-pointer block mx-auto" />
                  ) : r.verified ? <WaxSeal /> : <span className="uc-dot bg-white/15 block mx-auto"></span>}
                </span>
                <span className="min-w-0">
                  <span className="block font-mono-uc text-[11px] text-white/85 truncate" title={r.clinical_name || r.title}>
                    {r.clinical_name || r.title}
                  </span>
                  <span className="block font-mono-uc text-[9px] text-white/35 truncate">
                    {r.source} · {r.artifact_type}
                    {r.metadata?.parties ? ` · ${String(r.metadata.parties).slice(0, 20)}` : ""}
                    {r.duplicate_hits > 0 && <span className="text-amber-300" title={`${r.duplicate_hits} duplicate attempt(s) refused — this is the Master Record`}> · MASTER ×{r.duplicate_hits + 1}</span>}
                    {r.master_id && <span className="text-white/40" title="Duplicate content — mapped to its Master Record"> · dup→master</span>}
                  </span>
                </span>
                <span className={`font-mono-uc text-[8px] uppercase tracking-widest px-1.5 py-0.5 border w-fit truncate max-w-full ${vc}`} data-testid={`registry-venture-${r.id}`}>
                  {r.venture || "—"}
                </span>
                <span className="font-mono-uc text-[9px] uppercase tracking-widest text-white/55 truncate" data-testid={`registry-scope-${r.id}`}>{r.scope || "—"}</span>
                <span className="font-mono-uc text-[10px] text-white/40">
                  {r.timestamp ? new Date(r.timestamp).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "2-digit" }) : "—"}
                </span>
                <span className="font-mono-uc text-[10px] text-white/50" title="Cost of Production (estimated credits)">
                  {typeof r.cost_credits === "number" ? `${r.cost_credits.toFixed(2)}cr` : "—"}
                </span>
                <span className={`font-mono-uc text-[10px] ${r.roi_value ? "text-emerald-300" : "text-white/30"}`}
                      data-testid={`registry-roi-${r.id}`}
                      title={r.roi_source === "confirmed_revenue_ledger"
                        ? "LIVE ROI — confirmed revenue win auto-attached from the ledger to this producing artifact"
                        : "Realized value tied to this artifact"}>
                  {typeof r.roi_value === "number" ? `$${r.roi_value.toLocaleString()}` : "—"}
                </span>
                <span className="flex items-center gap-2 justify-end" onClick={(e) => e.stopPropagation()}>
                  {r.origin_url && (
                    <a href={r.origin_url} target="_blank" rel="noreferrer" data-testid={`registry-origin-${r.id}`}
                       className="text-white/40 hover:text-sky-300 transition-colors" title={`Origin: ${r.origin_url}`}>
                      <Link2 size={12}/>
                    </a>
                  )}
                  <button data-testid={`vault-download-${r.id}`} onClick={() => downloadArtifact(r)}
                          disabled={dlBusy === r.id}
                          className="text-white/40 hover:text-emerald-300 transition-colors disabled:opacity-50"
                          title="Open — download the actual file">
                    {dlBusy === r.id ? <Loader2 size={13} className="animate-spin"/> : <Download size={13}/>}
                  </button>
                  {r.source === "github" && r.metadata?.path && /\.(md|markdown|txt)$/i.test(r.metadata.path) && (
                    <button data-testid={`vault-quicklook-${r.id}`} onClick={() => quickLook(r)}
                            className="text-white/40 hover:text-amber-400 transition-colors" title="Quick-Look">
                      {quickBusy ? <Loader2 size={13} className="animate-spin"/> : <Eye size={13}/>}
                    </button>
                  )}
                  {r.url && (
                    <a href={r.url} target="_blank" rel="noreferrer" data-testid={`vault-open-${r.id}`}
                       className="text-white/40 hover:text-amber-400 transition-colors" title="Open in new tab">
                      <ExternalLink size={13}/>
                    </a>
                  )}
                </span>
              </div>
              {isOpen && (
                <div className="px-8 py-3 border-b border-white/5 bg-black/40" data-testid={`vault-brief-panel-${r.id}`}
                     onClick={(e) => e.stopPropagation()}>
                  {brief ? (
                    <div data-testid={`vault-brief-${r.id}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <ShieldCheck size={11} className="text-amber-400"/>
                        <span className="font-mono-uc text-[9px] uppercase tracking-widest text-amber-400">Auditor Mandate — Core Obligations</span>
                        <span className={`font-mono-uc text-[8px] uppercase tracking-widest px-1 py-0.5 border ${
                          brief.risk_level === "high" ? "border-rose-500/50 text-rose-300" :
                          brief.risk_level === "low" ? "border-emerald-500/50 text-emerald-300" :
                          "border-amber-500/50 text-amber-300"}`}>risk {brief.risk_level}</span>
                        {brief.evidence && <span className="font-mono-uc text-[8px] text-white/30">evidence: {brief.evidence}</span>}
                      </div>
                      <p className="text-xs text-white/70 mb-2">{brief.summary}</p>
                      <ul className="space-y-1">
                        {(brief.core_obligations || []).map((o, i) => (
                          <li key={i} className="text-[11px] text-white/60 flex gap-2">
                            <span className="text-amber-400/70 font-mono-uc">{String(i + 1).padStart(2, "0")}</span>{o}
                          </li>
                        ))}
                      </ul>
                      {(brief.mandates || []).length > 0 && (
                        <div className="mt-2 pt-2 border-t border-white/5" data-testid={`vault-mandates-${r.id}`}>
                          <span className="font-mono-uc text-[8px] uppercase tracking-widest text-rose-300">
                            Mandates filed to Action Hub — deadlines the Directorate defends
                          </span>
                          {(brief.mandates || []).map((m, i) => (
                            <div key={i} className="text-[11px] text-white/60 flex gap-2 mt-1">
                              <span className="text-rose-400/70 font-mono-uc">⚑</span>
                              {m.obligation}{m.date ? ` · due ${m.date}` : ""}{m.amount_usd ? ` · $${m.amount_usd.toLocaleString()}` : ""}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center gap-3">
                      <span className="font-mono-uc text-[10px] text-white/40">No intelligence brief on file for this artifact.</span>
                      <button data-testid={`vault-brief-btn-${r.id}`} onClick={() => issueBrief(r)} disabled={briefBusy === r.id}
                              className="uc-chip flex items-center gap-1.5 hover:border-amber-500/60 hover:bg-amber-500/10 text-amber-400 disabled:opacity-50">
                        {briefBusy === r.id ? <Loader2 size={10} className="animate-spin"/> : <ShieldCheck size={10}/>}
                        Issue Auditor Mandate
                      </button>
                    </div>
                  )}
                </div>
              )}
              </div>
            );})}
          </div>
        </section>

        {/* Sovereign Webhooks — autonomous next-step dispatches */}
        {webhooks.length > 0 && (
          <section className="uc-card p-4 mt-4" data-testid="sovereign-webhook-ledger">
            <div className="flex items-center gap-2 mb-3">
              <span className="uc-dot uc-pulsing bg-amber-400"></span>
              <span className="font-mono-uc text-[10px] uppercase tracking-widest text-white/60">Sovereign Webhooks · Autonomous Execution</span>
            </div>
            <div className="space-y-1.5">
              {webhooks.map((w) => (
                <div key={w.id} className="flex items-center gap-3 border border-white/5 px-3 py-2 flex-wrap" data-testid={`webhook-row-${w.id}`}>
                  <span className={`uc-dot ${w.status === "delivered" ? "bg-emerald-400" : w.status === "failed" ? "bg-rose-400" : "bg-white/25"}`}></span>
                  <span className="text-xs text-white/80 truncate max-w-[40%]" title={w.title}>{w.title}</span>
                  <span className="font-mono-uc text-[9px] uppercase tracking-widest text-sky-300">{w.target ? w.target.replace("_", " ") : "unrouted"}</span>
                  <span className={`font-mono-uc text-[9px] uppercase tracking-widest ${w.status === "delivered" ? "text-emerald-300" : w.status === "failed" ? "text-rose-300" : "text-white/40"}`}>{w.status}</span>
                  <span className="font-mono-uc text-[9px] text-white/35 ml-auto truncate max-w-[30%]" title={w.detail}>{w.detail}</span>
                </div>
              ))}
            </div>
          </section>
        )}
        <section className="uc-card p-5 mt-6" data-testid="capability-patches">
          <div className="font-mono-uc text-[10px] uppercase tracking-widest text-amber-400 mb-3">
            Execution Console · Capability Patches
          </div>
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <select data-testid="patch-agent-select" value={patchForm.agent}
                    onChange={(e) => setPatchForm({ ...patchForm, agent: e.target.value })}
                    className="bg-white/[0.04] border border-white/10 px-2 py-1.5 text-xs text-white/90 outline-none">
              {["executor", "scout", "auditor", "counsel", "sentinel", "controller", "envoy"].map((a) => (
                <option key={a} value={a}>{a}</option>
              ))}
            </select>
            <input data-testid="patch-capability-input" value={patchForm.capability}
                   onChange={(e) => setPatchForm({ ...patchForm, capability: e.target.value })}
                   placeholder="missing skill (e.g. video, complex_math)"
                   className="bg-white/[0.04] border border-white/10 px-3 py-1.5 text-xs text-white/90 outline-none w-64" />
            <button data-testid="patch-propose-btn" onClick={proposePatch}
                    className="uc-chip text-amber-400 hover:bg-amber-500/10">Oracle: Generate Patch</button>
          </div>
          {patches.length === 0 && (
            <div className="font-mono-uc text-[10px] text-white/35">No capability patches yet.</div>
          )}
          <div className="space-y-2">
            {patches.map((p) => (
              <div key={p.id} className="flex items-center gap-3 p-2.5 border border-white/5 flex-wrap"
                   data-testid={`patch-row-${p.id}`}>
                <span className={`uc-dot ${p.status === "applied" ? "bg-emerald-400" : "bg-amber-400"}`}></span>
                <span className="font-mono-uc text-[10px] uppercase text-white/70">{p.agent} ← {p.capability}</span>
                <span className="font-mono-uc text-[10px] text-white/40 truncate max-w-[30%]">{p.library}</span>
                {p.status === "applied" ? (
                  <span className="font-mono-uc text-[10px] text-emerald-300 ml-auto truncate max-w-[35%]"
                        title={p.evidence}>APPLIED · {p.evidence}</span>
                ) : (
                  <button data-testid={`patch-approve-${p.id}`} onClick={() => approvePatch(p)}
                          disabled={patchBusy === p.id}
                          className="uc-chip ml-auto text-emerald-300 border-emerald-500/40 hover:bg-emerald-500/10 disabled:opacity-50 flex items-center gap-1.5">
                    {patchBusy === p.id ? <Loader2 size={10} className="animate-spin"/> : <ShieldCheck size={10}/>}
                    Approve & Apply
                  </button>
                )}
              </div>
            ))}
          </div>
        </section>
        <div className="font-mono-uc text-[10px] text-white/30 mt-3" data-testid="vault-audit-note">
          Every read is logged to the vault audit trail. Gold seal = artifact evidenced in both GitHub and Gmail.
        </div>
      </div>

      {fileOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" data-testid="file-contract-modal">
          <div className="w-full max-w-2xl border border-white/15 bg-[#0c0c0c] p-5 max-h-[90vh] overflow-y-auto uc-scroll">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="font-mono-uc text-[10px] uppercase tracking-widest text-emerald-300">File Real Contract</div>
                <div className="font-serif-display text-lg">sovereign-vault / contracts</div>
              </div>
              <button data-testid="file-contract-close" onClick={() => setFileOpen(false)}
                      className="text-white/40 hover:text-white/80"><X size={16}/></button>
            </div>
            <div className="space-y-3">
              <input data-testid="file-contract-title" value={cf.title}
                     onChange={(e) => setCf({ ...cf, title: e.target.value })}
                     placeholder="Contract title (e.g. Master Services Agreement — Acme Corp)"
                     className="w-full bg-white/[0.04] border border-white/10 px-3 py-2 text-sm text-white/90 outline-none focus:border-emerald-500/50" />
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <input data-testid="file-contract-parties" value={cf.parties}
                       onChange={(e) => setCf({ ...cf, parties: e.target.value })}
                       placeholder="Parties (e.g. G. Bent / Acme Corp)"
                       className="bg-white/[0.04] border border-white/10 px-3 py-2 text-sm text-white/90 outline-none focus:border-emerald-500/50" />
                <input data-testid="file-contract-date" value={cf.signed_date}
                       onChange={(e) => setCf({ ...cf, signed_date: e.target.value })}
                       placeholder="Signed date YYYY-MM-DD"
                       className="bg-white/[0.04] border border-white/10 px-3 py-2 text-sm text-white/90 outline-none focus:border-emerald-500/50" />
                <select data-testid="file-contract-status" value={cf.status}
                        onChange={(e) => setCf({ ...cf, status: e.target.value })}
                        className="bg-white/[0.04] border border-white/10 px-3 py-2 text-sm text-white/90 outline-none focus:border-emerald-500/50">
                  <option value="SIGNED">SIGNED</option>
                  <option value="EXECUTED">EXECUTED</option>
                  <option value="APPROVED">APPROVED</option>
                </select>
              </div>
              <textarea data-testid="file-contract-body" value={cf.body}
                        onChange={(e) => setCf({ ...cf, body: e.target.value })}
                        placeholder="Paste the ACTUAL contract text here — payment terms, deadlines, dollar amounts. The Auditor never invents obligations; it briefs only what is written."
                        rows={10}
                        className="w-full bg-white/[0.04] border border-white/10 px-3 py-2 text-sm text-white/90 outline-none focus:border-emerald-500/50 uc-scroll" />
              <div className="flex items-center gap-3">
                <button data-testid="file-contract-submit" onClick={fileContract}
                        disabled={fileBusy || cf.title.trim().length < 3 || cf.body.trim().length < 20}
                        className="uc-chip flex items-center gap-1.5 text-emerald-300 border-emerald-500/40 hover:bg-emerald-500/10 disabled:opacity-40">
                  {fileBusy ? <Loader2 size={10} className="animate-spin"/> : <FilePlus2 size={10}/>}
                  Commit to GitHub Vault
                </button>
                <span className="font-mono-uc text-[10px] text-white/35">Then run Full Census to convert dates and liabilities into Live Mandates.</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {quick && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" data-testid="vault-quicklook-modal">
          <div className="w-full max-w-2xl border border-white/15 bg-[#0c0c0c] p-5 max-h-[85vh] overflow-y-auto uc-scroll">
            <div className="flex items-center gap-2 mb-3">
              <Eye size={13} className="text-amber-400"/>
              <span className="font-mono-uc text-[10px] uppercase tracking-widest text-white/70">{quick.path}</span>
              <button onClick={() => setQuick(null)} data-testid="vault-quicklook-close" className="ml-auto text-white/40 hover:text-white"><X size={15}/></button>
            </div>
            {Object.keys(quick.frontmatter || {}).length > 0 && (
              <div className="flex gap-2 flex-wrap mb-3">
                {Object.entries(quick.frontmatter).map(([k, v]) => (
                  <span key={k} className="uc-chip">{k}: {String(v).slice(0, 40)}</span>
                ))}
              </div>
            )}
            <pre className="border border-white/10 bg-black p-3 text-[11px] text-white/70 whitespace-pre-wrap font-mono">{quick.content}</pre>
            {quick.url && <a href={quick.url} target="_blank" rel="noreferrer" className="uc-chip mt-3 inline-flex items-center gap-1.5 hover:bg-white/5"><ExternalLink size={10}/> Open on GitHub</a>}
          </div>
        </div>
      )}
    </div>
  );
}
